import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  CheckCircle2,
  Loader2,
  Minus,
  Package,
  Plus,
  ShoppingBag,
  Store,
  Truck,
} from 'lucide-react';
import {
  commanderBoutique,
  fetchBoutiquePublic,
  fetchEvenementsPublics,
  type ArticleBoutique,
} from '../services/api';
import { euros } from '../utils/format';
import ChampAdresse from '../components/ChampAdresse';
import { vide } from '../utils/listes';

/** Une ligne du panier : un article dans une taille donnée. */
type LignePanier = {
  id: number;
  nom: string;
  taille: string;
  prix: number;
  partExpedition: number;
  quantite: number;
};

type PalierFrais = { jusqua: number | null; prix: number; libelle: string };

/**
 * La boutique publique : les goodies de l'association, du stock réel.
 *
 * Un supporter choisit ses articles, sa taille s'il y en a, laisse ses
 * coordonnées et règle par carte. Tout est expédié : le retrait sur place
 * supposait qu'une permanence soit tenue, et une commande réglée « quand on
 * se voit » finit en relance, stock immobilisé entre-temps.
 *
 * Les frais de port suivent le colis. Chaque article déclare ce qu'il y pèse,
 * et la grille du bureau en donne le prix — un porte-clefs seul ne coûte pas
 * l'envoi d'un carton de maillots.
 */
export default function BoutiquePage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['boutique-public'],
    queryFn: fetchBoutiquePublic,
    retry: 1,
  });
  /*
   * Le panier est indexé par article ET par taille.
   *
   * Deux tee-shirts du même modèle, l'un en M et l'autre en L, sont deux
   * lignes distinctes : ce sont deux vêtements différents pour celui qui
   * prépare le colis, même s'ils portent le même nom au catalogue.
   */
  const [panier, setPanier] = useState<Record<string, LignePanier>>({});

  // La taille en cours de sélection sur chaque carte, avant l'ajout au
  // panier : on choisit devant la photo, pas trois écrans plus loin.
  const [taillesChoisies, setTaillesChoisies] = useState<Record<number, string>>({});

  const retourCommande = useMemo(
    () => new URLSearchParams(window.location.search).get('commande'),
    []
  );
  const [etape, setEtape] = useState<'catalogue' | 'coordonnees'>('catalogue');
  const [form, setForm] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresseLigne1: '',
    adresseLigne2: '',
    codePostal: '',
    ville: '',
    commentaire: '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const articles: ArticleBoutique[] = data?.articles ?? [];
  const grille: PalierFrais[] = data?.fraisGrille ?? vide<PalierFrais>();

  const lignes = Object.values(panier);

  const totalArticles = useMemo(
    () => lignes.reduce((somme, l) => somme + l.quantite * l.prix, 0),
    [lignes]
  );

  const nbArticles = lignes.reduce((s, l) => s + l.quantite, 0);

  // Les frais suivent le colis, pas la commande : chaque article déclare ce
  // qu'il y pèse, et la grille du bureau donne le prix du total.
  const parts = lignes.reduce((s, l) => s + l.quantite * l.partExpedition, 0);
  const frais = useMemo(() => {
    if (parts <= 0) return 0;
    const palier = grille.find((p) => p.jusqua === null || parts <= p.jusqua);
    return palier?.prix ?? (grille.length ? Math.max(...grille.map((p) => p.prix)) : 0);
  }, [grille, parts]);

  const total = totalArticles + frais;

  /** Combien de cet article, toutes tailles confondues, sont déjà au panier. */
  const dejaPris = (article: ArticleBoutique) =>
    lignes.filter((l) => l.id === article.id).reduce((s, l) => s + l.quantite, 0);

  const changerQuantite = (article: ArticleBoutique, taille: string, delta: number) => {
    const cle = `${article.id}|${taille}`;
    setPanier((prev) => {
      const actuelle = prev[cle]?.quantite ?? 0;
      // Le stock est global à l'article : ce qui est déjà pris dans une autre
      // taille compte dans la limite.
      const autresTailles = Object.values(prev)
        .filter((l) => l.id === article.id && l.taille !== taille)
        .reduce((s, l) => s + l.quantite, 0);
      const plafond = Math.max(0, article.stock - autresTailles);
      const quantite = Math.max(0, Math.min(plafond, actuelle + delta));

      const suivant = { ...prev };
      if (quantite === 0) delete suivant[cle];
      else
        suivant[cle] = {
          id: article.id,
          nom: article.nom,
          taille,
          prix: article.prix,
          partExpedition: article.partExpedition ?? 1,
          quantite,
        };

      return suivant;
    });
  };

  const commander = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      const reponse = await commanderBoutique({
        nom: form.nom,
        email: form.email,
        telephone: form.telephone || undefined,
        adresseLigne1: form.adresseLigne1,
        adresseLigne2: form.adresseLigne2 || undefined,
        codePostal: form.codePostal,
        ville: form.ville,
        commentaire: form.commentaire || undefined,
        articles: lignes.map((l) => ({ id: l.id, taille: l.taille, quantite: l.quantite })),
      });

      // Direction Stripe : la commande n'est ferme qu'une fois la carte
      // passée, et le webhook la marquera payée au retour.
      if (reponse.url) {
        window.location.href = reponse.url;
        return;
      }

      setErreur("Le paiement n'a pas pu s'ouvrir : réessayez dans quelques minutes.");
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnvoi(false);
    }
  };

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Un échec réseau n'est pas une boutique fermée : le dire, et proposer de
  // réessayer, plutôt que d'annoncer à tort que rien n'est à vendre.
  if (isError) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 space-y-4">
        <p className="text-gray-600 dark:text-gray-300">
          La boutique n'a pas répondu — un souci de réseau, sans doute passager.
        </p>
        <button onClick={() => refetch()} className="btn-primary text-sm px-4 py-2">
          Réessayer
        </button>
      </div>
    );
  }

  if (!data?.active) {
    return <BoutiqueFermee />;
  }

  // Le retour de Stripe : la commande est réglée, ou elle a été abandonnée.
  if (retourCommande && retourCommande !== 'annulee') {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 max-w-xl mx-auto text-center space-y-4 pt-8">
        <CheckCircle2 className="w-14 h-14 text-primary-600 mx-auto" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Merci, c'est réglé !</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Référence{' '}
          <strong className="text-gray-900 dark:text-gray-100">{retourCommande}</strong>. Vous
          recevrez un courriel dès que votre colis partira.
        </p>
        <Link to="/" className="inline-block text-primary-700 dark:text-primary-300 font-semibold hover:underline">
          ← Retour à l'accueil
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <ShoppingBag className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">La boutique</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            T-shirts, écocups et autres couleurs de l'association — expédiés chez vous, réglés par carte.
          </p>
        </div>
      </div>

      {etape === 'catalogue' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((a) => (
              <div key={a.id} className="card overflow-hidden p-0 flex flex-col">
                {a.imageUrl ? (
                  <img src={a.imageUrl} alt={a.nom} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-primary-50 dark:bg-primary-900/20 grid place-items-center">
                    <Package className="w-10 h-10 text-primary-300" />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="font-semibold text-gray-900 dark:text-gray-100">{a.nom}</h2>
                    <p className="font-bold text-primary-700 dark:text-primary-300 whitespace-nowrap">{euros.format(a.prix)}</p>
                  </div>
                  {a.description && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 flex-1">{a.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.stock > 5 ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : a.stock > 0 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-gray-200 text-gray-500'}`}>
                      {a.stock > 5 ? 'En stock' : a.stock > 0 ? `Plus que ${a.stock} !` : 'Épuisé'}
                    </span>
                  </div>

                  {/* La taille se choisit ici, devant la photo, au moment où
                      l'on hésite encore — et seulement si l'article en a. */}
                  {a.stock > 0 && a.tailles.length > 0 && (
                    <div className="mt-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Taille</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {a.tailles.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setTaillesChoisies((prev) => ({ ...prev, [a.id]: t }))}
                            className={`px-2.5 py-1 rounded-lg border text-sm ${
                              taillesChoisies[a.id] === t
                                ? 'border-primary-600 bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 font-medium'
                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {a.stock > 0 && (
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {a.tailles.length > 0 && !taillesChoisies[a.id]
                          ? 'Choisissez une taille'
                          : dejaPris(a) > 0
                            ? `${dejaPris(a)} au panier`
                            : ''}
                      </span>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const taille = a.tailles.length > 0 ? (taillesChoisies[a.id] ?? '') : '';
                          const bloque = a.tailles.length > 0 && taille === '';
                          const auPanier = panier[`${a.id}|${taille}`]?.quantite ?? 0;

                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => changerQuantite(a, taille, -1)}
                                className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 grid place-items-center text-gray-600 dark:text-gray-300 disabled:opacity-40"
                                disabled={bloque || auPanier === 0}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-6 text-center font-semibold tabular-nums">{auPanier}</span>
                              <button
                                type="button"
                                onClick={() => changerQuantite(a, taille, 1)}
                                className="w-8 h-8 rounded-full bg-primary-600 text-white grid place-items-center disabled:opacity-40"
                                disabled={bloque || dejaPris(a) >= a.stock}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {articles.length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 sm:col-span-2 lg:col-span-3">La boutique est ouverte mais les rayons se remplissent : repassez bientôt !</p>
            )}
          </div>

          {nbArticles > 0 && (
            <div className="sticky bottom-20 lg:bottom-4 card flex items-center justify-between gap-3 shadow-lg border-primary-200 dark:border-primary-800">
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {nbArticles} article(s) — {euros.format(totalArticles)}
              </p>
              <button type="button" onClick={() => setEtape('coordonnees')} className="btn-primary px-5 py-2.5">
                Commander
              </button>
            </div>
          )}
        </>
      )}

      {etape === 'coordonnees' && (
        <div className="card max-w-xl space-y-4">
          <button type="button" onClick={() => setEtape('catalogue')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400">
            <ArrowLeft className="w-4 h-4" /> Modifier le panier
          </button>

          <div className="text-sm space-y-1">
            {lignes.map((l) => (
              <p key={`${l.id}|${l.taille}`} className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>
                  {l.quantite} × {l.nom}
                  {l.taille && <span className="text-gray-400"> — {l.taille}</span>}
                </span>
                <span className="tabular-nums">{euros.format(l.prix * l.quantite)}</span>
              </p>
            ))}
            <p className="flex justify-between text-gray-600 dark:text-gray-300">
              <span className="flex items-center gap-1.5">
                <Truck className="w-4 h-4" /> Expédition
              </span>
              <span className="tabular-nums">{euros.format(frais)}</span>
            </p>
            <p className="flex justify-between font-bold text-gray-900 dark:text-gray-100 border-t border-gray-100 dark:border-gray-800 pt-1.5"><span>Total</span><span className="tabular-nums">{euros.format(total)}</span></p>
          </div>

          <input className="input" placeholder="Votre nom *" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
          <input className="input" type="email" placeholder="Votre email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="input" placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
          {/* L'adresse en morceaux plutôt qu'en pavé : c'est ce qui permet de
              fabriquer une étiquette de transport sans qu'un bénévole la
              recopie — et une recopie d'adresse, c'est un colis perdu de
              temps en temps. */}
          <ChampAdresse
            valeur={form.adresseLigne1}
            onChange={(v) => setForm({ ...form, adresseLigne1: v })}
            onAdresseChoisie={(a) =>
              setForm({ ...form, adresseLigne1: a.ligne1, codePostal: a.codePostal, ville: a.ville })
            }
          />
          <input className="input" placeholder="Complément (bâtiment, appartement…)" value={form.adresseLigne2} onChange={(e) => setForm({ ...form, adresseLigne2: e.target.value })} />
          <div className="flex gap-2">
            <input
              className="input w-28 tabular-nums"
              inputMode="numeric"
              placeholder="Code postal *"
              value={form.codePostal}
              onChange={(e) => setForm({ ...form, codePostal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            />
            <input className="input flex-1" placeholder="Commune *" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
          </div>
          <textarea className="input" rows={2} placeholder="Un mot pour nous ?" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} />

          {erreur && <p className="text-sm text-red-600">{erreur}</p>}
          <button type="button" onClick={commander}
            disabled={
              envoi
              || !form.nom.trim()
              || !form.email.trim()
              || !form.adresseLigne1.trim()
              || form.codePostal.length !== 5
              || !form.ville.trim()
            }
            className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {envoi && <Loader2 className="w-4 h-4 animate-spin" />}
            Payer {euros.format(total)} par carte
          </button>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
            Aucun paiement en ligne : le règlement se fait à la remise, ou par virement avant expédition.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * La boutique fermée.
 *
 * Quelqu'un est venu acheter quelque chose et repart les mains vides : c'est
 * le seul moment de la visite où la page doit faire davantage que constater.
 * Une ligne de texte gris — « la boutique n'est pas ouverte » — laisse le
 * visiteur devant un cul-de-sac, et il ne revient pas.
 *
 * Alors l'écran dit trois choses. Que c'est temporaire, et pourquoi : le
 * bureau prépare, ce sont des bénévoles, cela se comprend. Que revenir en
 * vaudra la peine. Et surtout, il propose autre chose tout de suite — les
 * prochaines fêtes, puisque quelqu'un qui voulait un t-shirt de l'association
 * est exactement quelqu'un qui viendrait à sa fête.
 *
 * Aucune date d'ouverture n'est promise : nous ne la connaissons pas, et une
 * date annoncée puis manquée coûte plus que le silence.
 */
function BoutiqueFermee() {
  const { data: evenements } = useQuery({
    queryKey: ['evenements-publics'],
    queryFn: fetchEvenementsPublics,
    // La page se tient très bien sans : un échec ici ne doit pas remplacer un
    // message d'attente par un message d'erreur.
    retry: false,
  });

  const prochains = (Array.isArray(evenements) ? evenements : [])
    .filter((e: any) => e?.dateDebut && new Date(e.dateDebut) >= new Date())
    .sort((a: any, b: any) => a.dateDebut.localeCompare(b.dateDebut))
    .slice(0, 3);

  return (
    <div className="p-4 lg:p-0 pb-24 lg:pb-0">
      <div className="max-w-2xl mx-auto text-center pt-6 lg:pt-12 space-y-6">
        {/* Le rideau baissé, dessiné plutôt qu'écrit : on comprend l'état
            avant d'avoir lu la première ligne. */}
        <div className="relative mx-auto w-28 h-28">
          <div className="absolute inset-0 rounded-3xl bg-primary-50 dark:bg-primary-900/20" />
          <div className="absolute inset-0 grid place-items-center">
            <Store className="w-12 h-12 text-primary-600 dark:text-primary-300" />
          </div>
          <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 grid place-items-center shadow-sm">
            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </span>
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            La boutique rouvre bientôt
          </h1>
          <p className="text-gray-600 dark:text-gray-300 lg:text-lg">
            Le bureau prépare le prochain catalogue — les articles, les tailles, les photos. Tout
            cela se fait entre deux réunions et après le travail, alors le rideau reste baissé le
            temps que ce soit présentable.
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            Repassez dans quelques jours : la boutique s'ouvrira ici même, sans que vous ayez rien
            à faire.
          </p>
        </div>

        {/* Ce qu'on peut faire maintenant. Quelqu'un qui voulait un t-shirt
            de l'association est exactement quelqu'un qui viendrait à sa fête. */}
        {prochains.length > 0 && (
          <div className="card text-left space-y-3">
            <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary-600 shrink-0" />
              En attendant, voici ce qui se prépare
            </p>
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {prochains.map((e: any) => (
                <li key={e.id}>
                  <Link
                    to={`/evenements/${e.id}`}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5 group"
                  >
                    <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
                      {new Intl.DateTimeFormat('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                      }).format(new Date(e.dateDebut))}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-300">
                      {e.nom}
                    </span>
                    {e.lieu && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{e.lieu}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 pt-1">
          <Link to="/evenements" className="btn-primary inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> Voir les événements
          </Link>
          <Link to="/" className="btn-outline inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
